import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Cpu, Network, BarChart3, Brain, Radio, Zap, Building2 } from "lucide-react";
import ApplyDialog from "./ApplyDialog";
import { Button } from "../ui/button";
import { PROGRAMS } from "../../lib/constants";

const ICON_MAP = { Cpu, Network, BarChart3, Brain, Radio, Zap, Building2 };

const Programs = () => {
  return (
    <section id="programs" className="py-20 md:py-28 bg-megha-bg" data-testid="section-programs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-megha-accent">B.Tech Programs</div>
          <h2 className="mt-2 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-megha-text">
            Seven future-ready specializations.
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg">
            Choose from industry-aligned 4-year B.Tech programs designed for the AI-first, electrified, software-defined economy.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {PROGRAMS.map((p, i) => {
            const Icon = ICON_MAP[p.icon] || Cpu;
            const isFeatured = p.code === "BBC";
            return (
              <motion.div
                key={p.code}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className={`group relative rounded-3xl p-7 border transition-all duration-300 hover:-translate-y-1 ${
                  isFeatured
                    ? "bg-gradient-to-br from-megha-primary to-megha-accent text-white border-megha-primary shadow-xl shadow-blue-900/25"
                    : "bg-white border-slate-100 hover:border-megha-accent/40 hover:shadow-xl hover:shadow-blue-900/10"
                }`}
                data-testid={`card-program-${p.code.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      isFeatured ? "bg-megha-highlight text-megha-text" : "bg-megha-primary/10 text-megha-primary group-hover:bg-megha-primary group-hover:text-white"
                    } transition-colors`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>
                  <span
                    className={`text-[10px] font-extrabold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full ${
                      isFeatured ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {p.code}
                  </span>
                </div>

                <h3 className={`mt-6 font-heading font-extrabold text-xl leading-snug ${isFeatured ? "text-white" : "text-megha-text"}`}>
                  {p.title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${isFeatured ? "text-blue-50/90" : "text-slate-500"}`}>
                  {p.desc}
                </p>

                <div className={`mt-5 flex flex-wrap gap-1.5 ${isFeatured ? "" : ""}`}>
                  {p.careers.map((c) => (
                    <span
                      key={c}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                        isFeatured ? "bg-white/15 text-white border border-white/20" : "bg-slate-50 text-slate-600 border border-slate-100"
                      }`}
                    >
                      {c}
                    </span>
                  ))}
                </div>

                <div className={`mt-6 flex items-center justify-between pt-5 border-t ${isFeatured ? "border-white/20" : "border-slate-100"}`}>
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${isFeatured ? "text-blue-50" : "text-slate-500"}`}>
                    <Clock className="w-3.5 h-3.5" /> 4 Years · Full-time
                  </div>
                  <ApplyDialog
                    trigger={
                      <button
                        data-testid={`btn-program-apply-${p.code.toLowerCase().replace(/[^a-z]/g, "")}`}
                        className={`inline-flex items-center gap-1 text-sm font-bold transition-all ${
                          isFeatured ? "text-megha-highlight hover:text-white" : "text-megha-primary hover:text-megha-accent"
                        }`}
                      >
                        Apply <ArrowUpRight className="w-4 h-4" />
                      </button>
                    }
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Programs;
